/**
 * @file components/audit-tasks/preparation/CaseDocumentsCard.tsx
 * @description 應附文件內聯卡片（序122/66：稽核員於文件審查階段可上傳 / 替換 / 刪除）。
 *
 * 序66/122 解鎖語義：
 *   - 稽核員（本案稽核團隊成員）在文件審查時可上傳應附文件（POST /api/cases/:id/documents，
 *     後端行級授權：case.dispatch 或本案團隊成員）；
 *   - 誤傳可【替換】（PATCH 帶新 fileId 覆蓋原表單）或【刪除】；
 *   - 現場稽核階段亦可檢視（PhaseField 走離線文件包，本卡片為在線入口）。
 *
 * 資料來源：GET /api/cases/:id（case detail），其 response.item.documents 即為清單。
 */

import { useState } from 'react';
import { Button, Card, Empty, Popconfirm, Select, Space, Table, Typography, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Trash2, Upload as UploadIcon } from 'lucide-react';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import { uploadFile } from '@/lib/file-upload';
import { FileLink } from '@/components/common/FileLink';
import { fetchWithOfflineCache } from '@/pwa/offline-api-cache';
import { CASE_DOC_CATEGORIES } from '@/lib/case-doc-categories';

/**
 * 案件文件（對應 GET /api/cases/:id 回傳的 documents 陣列項）。
 *
 * 後端 LEFT JOIN files 帶出原檔名 / 大小 / mime；軟刪除 file 後可能為 null。
 */
interface CaseDocument {
  id: number;
  docType: string;
  fileId: number;
  filename: string | null;
  sizeBytes: number | null;
  mimeType: string | null;
  createdAt: string;
}

/** GET /api/cases/:id 的 response shape（只列本元件需要的欄位） */
interface CaseDetailResponse {
  item: {
    documents: CaseDocument[];
  };
}

interface Props {
  /** cases.id */
  caseId: number;
  /** 唯讀模式（如管理組現場唯讀視角）；預設可操作 */
  readOnly?: boolean;
}

/**
 * 將位元組格式化為 B / KB / MB。
 */
function formatSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * 應附文件 Card：展示 + 上傳 + 替換 + 刪除（序122/66）。
 */
export function CaseDocumentsCard({ caseId, readOnly = false }: Props) {
  const qc = useQueryClient();
  // 上傳分類（11 類 SOP 應附文件），選定後上傳
  const [uploadDocType, setUploadDocType] = useState<string>(CASE_DOC_CATEGORIES[0]!.key);
  const [uploading, setUploading] = useState(false);
  // 替換中的行 id（該行按鈕顯示 loading）
  const [replacingId, setReplacingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery<CaseDetailResponse>({
    queryKey: ['case-detail-documents', caseId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<CaseDetailResponse>(`/api/cases/${caseId}`, {
        scope: 'case-detail',
      });
      return res.data;
    },
  });

  const documents = data?.item.documents ?? [];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['case-detail-documents', caseId] });
    void qc.invalidateQueries({ queryKey: ['case-detail', caseId] });
  };

  /** 新上傳應附文件（序66.1） */
  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const uploaded = await uploadFile(file, {
        scope: 'case',
        ownerType: 'case',
        ownerId: caseId,
      });
      await apiClient.post(`/api/cases/${caseId}/documents`, {
        fileId: uploaded.id,
        docType: uploadDocType,
      });
      message.success(`已上傳：${uploaded.filename}`);
      invalidate();
    } catch (err) {
      console.error('[CaseDocumentsCard] upload failed', err);
      message.error(err instanceof Error ? err.message : '上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  /** 替換覆蓋原表單（序66.4） */
  const handleReplace = async (doc: CaseDocument, file: File) => {
    setReplacingId(doc.id);
    try {
      const uploaded = await uploadFile(file, {
        scope: 'case',
        ownerType: 'case',
        ownerId: caseId,
      });
      await apiClient.patch(`/api/cases/${caseId}/documents/${doc.id}`, { fileId: uploaded.id });
      message.success(`已覆蓋：${uploaded.filename}`);
      invalidate();
    } catch (err) {
      console.error('[CaseDocumentsCard] replace failed', err);
      message.error(err instanceof Error ? err.message : '替換失敗');
    } finally {
      setReplacingId(null);
    }
  };

  /** 刪除（誤傳補救） */
  const removeMutation = useMutation({
    mutationFn: (docId: number) => apiClient.delete(`/api/cases/${caseId}/documents/${docId}`),
    onSuccess: () => {
      message.success('已刪除');
      invalidate();
    },
    onError: (err: Error) => {
      console.error('[CaseDocumentsCard] remove failed', err);
      message.error(err.message ?? '刪除失敗');
    },
  });

  const cols: ColumnsType<CaseDocument> = [
    {
      title: '類型',
      dataIndex: 'docType',
      width: 140,
    },
    {
      title: '檔名',
      dataIndex: 'filename',
      ellipsis: true,
      render: (n: string | null, r) =>
        n ? <FileLink fileId={r.fileId} filename={n} compact /> : '—',
    },
    {
      title: '大小',
      dataIndex: 'sizeBytes',
      width: 100,
      render: (b: number | null) => formatSize(b),
    },
    {
      title: '上傳時間',
      dataIndex: 'createdAt',
      width: 160,
      render: (d: string) => new Date(d).toLocaleString('zh-TW'),
    },
    // 序66：替換（覆蓋原表單）/ 刪除（誤傳補救）
    ...(readOnly
      ? []
      : ([
          {
            title: '操作',
            key: 'actions',
            width: 130,
            render: (_: unknown, doc: CaseDocument) => (
              <Space size={4}>
                <Upload
                  showUploadList={false}
                  beforeUpload={(file) => {
                    void handleReplace(doc, file);
                    return false; // 阻止 antd 內建 XHR，改走 OSS 直傳
                  }}
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<RefreshCw size={12} />}
                    loading={replacingId === doc.id}
                  >
                    替換
                  </Button>
                </Upload>
                <Popconfirm
                  title="刪除此應附文件？"
                  description="誤傳補救用；刪除後可重新上傳。"
                  okText="刪除"
                  cancelText="取消"
                  onConfirm={() => removeMutation.mutate(doc.id)}
                >
                  <Button type="link" size="small" danger icon={<Trash2 size={12} />} />
                </Popconfirm>
              </Space>
            ),
          },
        ] as ColumnsType<CaseDocument>)),
  ];

  return (
    <Card
      size="small"
      title="應附文件"
      extra={
        readOnly ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            唯讀
          </Typography.Text>
        ) : (
          <Space size={8}>
            <Select
              size="small"
              style={{ width: 200 }}
              value={uploadDocType}
              onChange={setUploadDocType}
              options={CASE_DOC_CATEGORIES.map((c) => ({ value: c.key, label: c.label }))}
            />
            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                void handleUpload(file);
                return false;
              }}
            >
              <Button size="small" icon={<UploadIcon size={12} />} loading={uploading}>
                上傳
              </Button>
            </Upload>
          </Space>
        )
      }
    >
      {/* 2026-09-13 回報序19：補操作提示——先在右上角選擇應附文件項目，再上傳對應文件 */}
      {!readOnly && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          請勾選欲處理的應附文件項目後，上傳對應文件。（於右上角下拉選擇文件項目，再點擊「上傳」）
        </Typography.Paragraph>
      )}
      {isLoading ? (
        <Typography.Text>載入中…</Typography.Text>
      ) : documents.length ? (
        <Table<CaseDocument>
          rowKey="id"
          dataSource={documents}
          columns={cols}
          pagination={false}
          size="small"
        />
      ) : (
        <Empty description="尚無應附文件" />
      )}
    </Card>
  );
}
